// AI 키 관리(docs/build/16 §4-5 / 결정·한계 docs/build/00 S4-3) — 환경 저장·불러오기 탭.
//
// 설계 원칙(왜 이렇게 답답하게 만들었나):
// - **쓰기 전용**: 서버가 값을 절대 돌려주지 않는다. 화면에도 끝 4자만 뜬다.
//   관리자 토큰 하나가 새면 키 4개가 다 새는 상황을 막기 위해서다.
// - **서버 시크릿 우선**: wrangler secret 으로 고정된 항목은 여기서 못 바꾼다(고쳐도 안 먹으므로).
// - 저장은 AES-GCM 암호화. 마스터 키가 서버에 없으면 이 패널이 통째로 비활성이다(평문 폴백 없음).
import { useEffect, useState } from "react";
import {
  AI_PROVIDERS,
  AI_PROVIDER_INFO,
  AI_PRICING_URL,
  mergeModelRows,
  AI_PRICE_ASOF,
  formatDraftCostKrw,
  type AiProvider,
} from "@greg-chick/shared";
import { loadSecrets, saveSecret, deleteSecret, fetchLiveModels, type SecretStatus } from "../lib/contentApi";
import { ActionButton } from "./ActionButton";

const SOURCE_LABEL: Record<SecretStatus["source"], string> = {
  env: "서버 시크릿으로 고정",
  stored: "설정됨",
  none: "없음",
};

function KeyRow({
  label,
  name,
  status,
  disabled,
  onSaved,
}: {
  label: string;
  name: string;
  status: SecretStatus;
  disabled: boolean;
  onSaved: (s: SecretStatus) => void;
}) {
  const [value, setValue] = useState("");
  const fixed = status.source === "env";

  // 진행 표시·확인·실패 표시는 ActionButton 이 한다 — 여기선 일만 하고 실패는 던진다.
  async function save() {
    onSaved(await saveSecret(name, value));
    setValue(""); // 입력칸에 키를 남겨두지 않는다(어깨너머·스크린샷)
  }
  async function remove() {
    await deleteSecret(name);
    onSaved({ name, source: "none" });
  }

  return (
    <div className="secret-row">
      <div className="secret-row__head">
        <strong>{label}</strong>
        <span className="secret-row__state" data-source={status.source}>
          {SOURCE_LABEL[status.source]}
          {status.hint ? ` ${status.hint}` : ""}
        </span>
      </div>
      {fixed ? (
        <p className="hint">서버에 직접 설정돼 있어 여기서는 바꿀 수 없어요.</p>
      ) : (
        <div className="row">
          <input
            type="password"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={status.source === "stored" ? "새 키로 바꾸려면 입력" : "키를 붙여넣으세요"}
            disabled={disabled}
            autoComplete="off"
            spellCheck={false}
          />
          <ActionButton className="btn btn--primary" busyLabel="저장 중…" doneLabel="저장됨" disabled={disabled || !value.trim()} onAction={save}>
            저장
          </ActionButton>
          {status.source === "stored" ? (
            <ActionButton
              busyLabel="지우는 중…"
              doneLabel="지움"
              confirm={`${label} 키를 지울까요? 그 AI 는 초안 목록에서 사라져요.`}
              onAction={remove}
            >
              삭제
            </ActionButton>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function SecretsPanel() {
  const [enabled, setEnabled] = useState(false);
  const [secrets, setSecrets] = useState<SecretStatus[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    void loadSecrets().then((r) => {
      setEnabled(r.enabled);
      setSecrets(r.secrets);
      setLoaded(true);
    });
  }, []);

  const statusOf = (name: string): SecretStatus => secrets.find((s) => s.name === name) ?? { name, source: "none" };
  const update = (s: SecretStatus) => setSecrets((prev) => [...prev.filter((x) => x.name !== s.name), s]);

  if (!loaded) return null;

  return (
    <section className="panel">
      <header className="panel__head">
        <h2>🤖 AI 키</h2>
      </header>
      <p className="hint">
        굿즈 <b>✨AI 초안</b>과 친구의 <b>💬 자유 대화</b>에 쓰는 키예요. <b>넣은 것만</b> 각 화면에서 고를 수 있어요.
        자유 대화는 Claude·GPT·Gemini를 지원하고, 키가 없으면 승인된 질문 버튼으로 돌아가요.
      </p>
      <p className="hint">
        🔒 키는 암호화해서 저장하고, <b>저장한 뒤에는 아무에게도 다시 보여주지 않아요</b>(끝 4자리만 표시). 값이 궁금하면
        발급처에서 새로 만드세요.
      </p>

      {!enabled ? (
        <p className="err">
          서버에 암호화 키(SECRETS_KEY)가 없어서 저장할 수 없어요. 배포할 때 <code>wrangler secret put SECRETS_KEY</code> 를 한 번만
          설정하면 이 화면이 열려요. (그 전까지는 개발자가 키를 직접 넣어야 해요)
        </p>
      ) : null}

      <div className="secret-list">
        {AI_PROVIDERS.map((p) => (
          <KeyRow
            key={p}
            label={AI_PROVIDER_INFO[p].label}
            name={AI_PROVIDER_INFO[p].keyEnv}
            status={statusOf(AI_PROVIDER_INFO[p].keyEnv)}
            disabled={!enabled}
            onSaved={update}
          />
        ))}
      </div>

      <details className="secret-adv">
        <summary>쓸 모델 고르기 · 가격 보기</summary>
        <p className="hint">
          같은 AI 안에서도 <b>똑똑한 모델일수록 비싸요</b>. 굿즈 초안 하나에 드는 값을 함께 적어뒀어요. 안 고르면 기본값(권장)을
          씁니다. 가격은 <b>{AI_PRICE_ASOF} 기준 공개가</b>라 바뀔 수 있어요 — 실제 청구는 각 AI 회사 계정 기준이에요.
        </p>
        {AI_PROVIDERS.map((p) => (
          <ModelPicker
            key={p}
            provider={p}
            status={statusOf(AI_PROVIDER_INFO[p].modelEnv)}
            disabled={!enabled}
            onSaved={update}
          />
        ))}
      </details>
    </section>
  );
}

/** 공급자별 모델 선택 — 목록가보다 "초안 1건 얼마"가 운영자에게 실질적이라 그걸 앞세운다. */
function ModelPicker({
  provider,
  status,
  disabled,
  onSaved,
}: {
  provider: AiProvider;
  status: SecretStatus;
  disabled: boolean;
  onSaved: (s: SecretStatus) => void;
}) {
  const info = AI_PROVIDER_INFO[provider];
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fixed = status.source === "env";

  // 계정에 실제로 있는 모델(새로고침 버튼으로 조회). undefined = 아직 조회 안 함.
  const [liveIds, setLiveIds] = useState<string[] | undefined>();
  const models = mergeModelRows(provider, liveIds);
  const missing = models.filter((m) => m.known && m.live === false);

  // 모델 id 는 비밀이 아니라 서버가 실값을 준다 → 새로고침해도 고른 모델이 유지된다.
  // (예전엔 키와 똑같이 끝 4자만 와서 선택이 기본값으로 되돌아가 보였다)
  const [picked, setPicked] = useState(status.value ?? info.defaultModel);
  useEffect(() => {
    setPicked(status.value ?? info.defaultModel);
  }, [status.value, info.defaultModel]);

  async function apply(id: string) {
    setPicked(id); // 낙관적 — 라디오가 즉시 반응해야 누른 게 먹혔는지 안다
    setBusy(true);
    setErr(null);
    try {
      await saveSecret(info.modelEnv, id);
      // 방금 저장한 값을 알고 있으니 그대로 올려 부모 상태와 서버 상태를 일치시킨다.
      onSaved({ name: info.modelEnv, source: "stored", hint: `…${id.slice(-4)}`, value: id });
    } catch (e) {
      setPicked(status.value ?? info.defaultModel); // 실패했으면 되돌린다(안 그러면 저장된 척)
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="model-block">
      <div className="model-block__head">
        <strong>{info.label}</strong>
        <span className="hint">
          {status.source === "stored" ? "직접 고른 모델 사용 중" : fixed ? "서버에서 고정" : "기본값 사용 중"}
        </span>
        <ActionButton
          busyLabel="확인 중…"
          doneLabel="확인함"
          disabled={disabled}
          onAction={async () => setLiveIds(await fetchLiveModels(provider))}
        >
          🔄 모델 목록 새로고침
        </ActionButton>
        <a className="model-block__link" href={AI_PRICING_URL[provider]} target="_blank" rel="noopener noreferrer">
          공식 가격표 ↗
        </a>
      </div>
      {/* 조회 결과를 사람 말로 요약 — 표만 바뀌면 뭐가 달라졌는지 모른다 */}
      {liveIds ? (
        <p className="hint">
          {missing.length
            ? `⚠️ ${missing.map((m) => m.label).join(", ")} 은(는) 이 계정에서 쓸 수 없어요. 고르면 실패해요.`
            : "✅ 아래 모델을 모두 쓸 수 있어요."}
          {models.some((m) => !m.known) ? " 새로 나온 모델도 찾았어요(가격은 공식 가격표에서 확인하세요)." : ""}
        </p>
      ) : null}
      <table className="model-table">
        <thead>
          <tr>
            <th />
            <th>모델</th>
            <th>초안 1건</th>
            <th>입력 / 출력 (100만 토큰당)</th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => (
            <tr key={m.id} data-on={picked === m.id ? "true" : undefined}>
              <td>
                <input
                  type="radio"
                  name={`model-${provider}`}
                  checked={picked === m.id}
                  disabled={disabled || busy || fixed}
                  onChange={() => void apply(m.id)}
                  aria-label={`${m.label} 사용`}
                />
              </td>
              <td>
                <b>{m.label}</b>
                {m.info?.note ? <span className="hint"> {m.info.note}</span> : null}
                {/* 새로고침으로 계정에 없다고 확인된 모델 — 고르면 404 로 실패한다 */}
                {m.live === false ? <span className="model-table__gone"> 이 계정에 없음</span> : null}
                {!m.known ? <span className="model-table__new"> 새 모델</span> : null}
              </td>
              <td className="model-table__cost">
                {/* 가격은 카탈로그가 유일한 출처 — 어떤 공급자도 API 로 가격을 주지 않는다 */}
                {m.info ? formatDraftCostKrw(m.info) : <span className="hint">가격 미상</span>}
              </td>
              <td className="hint">
                {m.info ? (
                  <>
                    ${m.info.inputPer1M} / ${m.info.outputPer1M}
                    {m.info.contextK ? ` · ${m.info.contextK}K` : ""}
                  </>
                ) : (
                  "공식 가격표에서 확인"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {status.source === "stored" ? (
        <ActionButton busyLabel="되돌리는 중…" doneLabel="되돌림" onAction={async () => { await deleteSecret(info.modelEnv); setPicked(info.defaultModel); onSaved({ name: info.modelEnv, source: "none" }); }}>
          기본값으로 되돌리기
        </ActionButton>
      ) : null}
      {err ? <p className="err">{err}</p> : null}
    </div>
  );
}
